/**
 * TAR Archive Utility
 * 
 * Creates TAR archives from File objects for directory uploads.
 * Implements USTAR (POSIX.1-1988) format for maximum compatibility.
 * 
 * STREAMING IMPLEMENTATION:
 * For large directories (6GB+), uses streaming writes to OPFS to avoid
 * memory exhaustion. Files are read in slices and written incrementally
 * rather than accumulated in memory.
 */

import { getOriginPrivateDirectory } from 'native-file-system-adapter';

// Chunk size for streaming file reads (4MB - balances memory vs I/O overhead)
const STREAM_CHUNK_SIZE = 4 * 1024 * 1024;

/**
 * TAR file header structure (512 bytes)
 * Based on USTAR format specification
 */
interface TarHeader {
  name: string;        // File name (100 bytes)
  mode: string;        // File mode (8 bytes octal)
  uid: string;         // Owner user ID (8 bytes octal)
  gid: string;         // Owner group ID (8 bytes octal)
  size: string;        // File size (12 bytes octal)
  mtime: string;       // Modification time (12 bytes octal)
  checksum: string;    // Header checksum (8 bytes octal)
  typeflag: string;    // File type (1 byte)
  linkname: string;    // Link name (100 bytes)
  magic: string;       // USTAR indicator (6 bytes)
  version: string;     // USTAR version (2 bytes)
  uname: string;       // Owner user name (32 bytes)
  gname: string;       // Owner group name (32 bytes)
  devmajor: string;    // Device major number (8 bytes octal)
  devminor: string;    // Device minor number (8 bytes octal)
  prefix: string;      // Filename prefix (155 bytes)
}

/**
 * Convert number to octal string with specified length
 */
function toOctal(value: number, length: number): string {
  const octal = value.toString(8);
  return octal.padStart(length - 1, '0') + ' ';
}

/**
 * Pad string to specified length with null bytes
 */
function padString(str: string, length: number): Uint8Array {
  const buffer = new Uint8Array(length);
  const encoder = new TextEncoder();
  const encoded = encoder.encode(str);
  buffer.set(encoded.slice(0, length));
  return buffer;
}

/**
 * Calculate checksum for TAR header
 */
function calculateChecksum(header: Uint8Array): number {
  let sum = 0;
  for (let i = 0; i < 512; i++) {
    if (i >= 148 && i < 156) {
      // Checksum field itself is treated as spaces
      sum += 32;
    } else {
      sum += header[i];
    }
  }
  return sum;
}

/**
 * Create a TAR header for a file
 */
function createTarHeader(filename: string, filesize: number, mtime: number): Uint8Array {
  const header = new Uint8Array(512);
  let offset = 0;

  // File name (100 bytes) - truncate if too long
  const nameBytes = padString(filename.slice(0, 100), 100);
  header.set(nameBytes, offset);
  offset += 100;

  // File mode (8 bytes) - default 0644 (rw-r--r--)
  header.set(padString(toOctal(0o644, 8), 8), offset);
  offset += 8;

  // Owner user ID (8 bytes) - default 0
  header.set(padString(toOctal(0, 8), 8), offset);
  offset += 8;

  // Owner group ID (8 bytes) - default 0
  header.set(padString(toOctal(0, 8), 8), offset);
  offset += 8;

  // File size (12 bytes octal)
  header.set(padString(toOctal(filesize, 12), 12), offset);
  offset += 12;

  // Modification time (12 bytes octal) - Unix timestamp
  header.set(padString(toOctal(Math.floor(mtime / 1000), 12), 12), offset);
  offset += 12;

  // Checksum (8 bytes) - calculate after header is built
  const checksumOffset = offset;
  header.set(padString('        ', 8), offset); // Placeholder spaces
  offset += 8;

  // Type flag (1 byte) - '0' for regular file
  header.set(padString('0', 1), offset);
  offset += 1;

  // Link name (100 bytes) - empty for regular files
  header.set(padString('', 100), offset);
  offset += 100;

  // USTAR magic (6 bytes)
  header.set(padString('ustar', 6), offset);
  offset += 6;

  // USTAR version (2 bytes) - '00'
  header.set(padString('00', 2), offset);
  offset += 2;

  // Owner user name (32 bytes) - 'moby'
  header.set(padString('moby', 32), offset);
  offset += 32;

  // Owner group name (32 bytes) - 'moby'
  header.set(padString('moby', 32), offset);
  offset += 32;

  // Device major number (8 bytes) - '0'
  header.set(padString(toOctal(0, 8), 8), offset);
  offset += 8;

  // Device minor number (8 bytes) - '0'
  header.set(padString(toOctal(0, 8), 8), offset);
  offset += 8;

  // Filename prefix (155 bytes) - empty
  header.set(padString('', 155), offset);

  // Calculate and set checksum
  const checksum = calculateChecksum(header);
  header.set(padString(toOctal(checksum, 7), 7), checksumOffset);

  return header;
}

/**
 * Pad file data to 512-byte block boundary
 */
function padToBlockSize(data: Uint8Array): Uint8Array {
  const blockSize = 512;
  const remainder = data.length % blockSize;
  if (remainder === 0) {
    return data;
  }
  
  const paddingSize = blockSize - remainder;
  const padded = new Uint8Array(data.length + paddingSize);
  padded.set(data);
  return padded;
}

/**
 * Create TAR archive from File objects using STREAMING to OPFS
 * 
 * This implementation writes directly to OPFS to handle large directories (6GB+)
 * without exhausting browser memory. Files are read in 4MB slices and written
 * incrementally to avoid the memory accumulation issue.
 * 
 * @param files - Array of File objects to include in TAR
 * @param progressCallback - Optional callback for progress updates
 * @returns Object containing File and OPFS path for rename optimization
 */
export async function createTarArchive(
  files: File[],
  progressCallback?: (progress: { processedFiles: number; totalFiles: number; processedBytes: number; totalBytes: number }) => void
): Promise<{ file: File; opfsPath: string }> {
  console.log(`[TAR_STREAM] Creating streaming archive from ${files.length} files`);
  
  let totalBytes = 0;
  let processedBytes = 0;
  
  // Calculate total size
  for (const file of files) {
    totalBytes += file.size;
  }
  
  console.log(`[TAR_STREAM] Total size to archive: ${(totalBytes / 1024 / 1024 / 1024).toFixed(2)} GB`);
  
  // Generate unique filename for the TAR in OPFS
  const tarFilename = `tar_archive_${Date.now()}.tar`;
  
  // Get OPFS root and create writable stream
  const opfsRoot = await getOriginPrivateDirectory();
  const fileHandle = await opfsRoot.getFileHandle(tarFilename, { create: true });
  
  // @ts-ignore - createWritable exists on FileSystemFileHandle
  const writable = await fileHandle.createWritable();
  
  try {
    // Process each file with streaming writes
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Extract relative path from webkitRelativePath or use name
      const filename = (file as any).webkitRelativePath || file.name;
      
      console.log(`[TAR_STREAM] Adding file ${i + 1}/${files.length}: ${filename} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
      
      // Write TAR header (512 bytes)
      const header = createTarHeader(filename, file.size, file.lastModified);
      await writable.write(header);
      
      // Stream file data in chunks to avoid memory exhaustion
      let fileOffset = 0;
      while (fileOffset < file.size) {
        const chunkSize = Math.min(STREAM_CHUNK_SIZE, file.size - fileOffset);
        const slice = file.slice(fileOffset, fileOffset + chunkSize);
        const chunkBuffer = await slice.arrayBuffer();
        await writable.write(new Uint8Array(chunkBuffer));
        
        fileOffset += chunkSize;
        processedBytes += chunkSize;
        
        // Report progress during large file streaming
        if (progressCallback) {
          progressCallback({
            processedFiles: i,
            totalFiles: files.length,
            processedBytes,
            totalBytes
          });
        }
        
        // Yield to event loop periodically to prevent UI freeze
        if (fileOffset % (STREAM_CHUNK_SIZE * 4) === 0) {
          await new Promise(resolve => setTimeout(resolve, 0));
        }
      }
      
      // Write padding to align to 512-byte boundary
      const remainder = file.size % 512;
      if (remainder !== 0) {
        const paddingSize = 512 - remainder;
        const padding = new Uint8Array(paddingSize);
        await writable.write(padding);
      }
      
      // Report file completion progress
      if (progressCallback) {
        progressCallback({
          processedFiles: i + 1,
          totalFiles: files.length,
          processedBytes,
          totalBytes
        });
      }
    }
    
    // Write end-of-archive marker (two 512-byte blocks of zeros)
    const endMarker = new Uint8Array(1024);
    await writable.write(endMarker);
    
    // Close the writable stream
    await writable.close();
    
    console.log(`[TAR_STREAM] Archive created: ${tarFilename} (${(processedBytes / 1024 / 1024).toFixed(2)} MB written to OPFS)`);
    
    // Read the file back from OPFS as a File object
    const opfsFile = await fileHandle.getFile();
    
    // Return File + OPFS path for rename optimization
    // Provider can rename this OPFS file to rootHash key instead of copying again
    // This eliminates the 2nd 80GB write for directory transfers
    return {
      file: new File([opfsFile], tarFilename, { type: 'application/x-tar' }),
      opfsPath: tarFilename
    };
    
  } catch (error) {
    // Clean up on error - close stream and remove partial file
    try {
      await writable.close();
    } catch (closeError) {
      console.warn('[TAR_STREAM] Error closing writable stream:', closeError);
    }
    
    try {
      await opfsRoot.removeEntry(tarFilename);
      console.log(`[TAR_STREAM] Cleaned up partial TAR file: ${tarFilename}`);
    } catch (removeError) {
      console.warn('[TAR_STREAM] Error removing partial TAR file:', removeError);
    }
    
    throw error;
  }
}

/**
 * Extract directory name from files for TAR filename
 */
export function getDirectoryName(files: File[]): string {
  if (files.length === 0) {
    return 'archive';
  }
  
  // Try to get directory name from webkitRelativePath
  const firstFile = files[0] as any;
  if (firstFile.webkitRelativePath) {
    const parts = firstFile.webkitRelativePath.split('/');
    if (parts.length > 1) {
      return parts[0]; // First part is the directory name
    }
  }
  
  // Fallback to timestamp-based name
  return `directory_${Date.now()}`;
}
